import { createFileRoute, Outlet } from '@tanstack/react-router';
import { useIxcPortfolioV1AssetBalanceQueryServiceAssetBalanceGet } from '@interxfi/api-client/generated/hooks/ixc-portfolio-v1-asset-balance-query-service-asset-balance-get';
import { PageLayout } from '@interxfi/app-components/components/layout/page-layout/page-layout';
import { type AggregateBreadcrumb, buildBreadcrumb } from '@interxfi/app-components/components/router-breadcrumbs/router-breadcrumbs';
import { CopyButton } from '@interxfi/design-system/components/copy-button/copy-button';
import { getShortenedId } from '@interxfi/design-system/components/format/id/shared';
import { Skeleton } from '@interxfi/design-system/components/loading/skeleton';
import { exhaustiveCheck } from '@interxfi/utilities/types/exhaustive';
import { AssetLogoBreadcrumb } from '../../../../../../../../../../../features/asset/logo/asset-logo-breadcrumb';
import { getAssetCusip, getAssetName, getAssetSymbol } from '../../../../../../../../../../../features/asset/utils';
import { PortfolioAssetBalanceEntityMenu } from '../../../../../../../../../../../features/portfolio/asset-balance/portfolio-asset-balance-entity-menu';
import { getPortfolioAssetBalanceDetailPath } from '../../../../../../../../../../../features/portfolio/asset-balance/portfolio-asset-balance-routing';
import { PortfolioAssetBalanceStatusPill } from '../../../../../../../../../../../features/portfolio/asset-balance/portfolio-asset-balance-status-pill';
import { AssetBalanceBalanceBar } from '../../../../../../../../../../../features/registration/account/account-balance-bar/asset-balance-balance-bar';
import { RegistrationAccountPageHeader } from '../../../../../../../../../../../features/registration/account/registration-account-page-header';
import { buildDefaultErrorMessageGetter, useErrorHandler } from '../../../../../../../../../../../util/error-handler';
import { getPageHead } from '../../../../../../../../../../../util/routing/meta';

function AssetBalanceBreadcrumb({ accountId, assetId }: { accountId: string; assetId: string }) {
  const { data, error, isPending } = useIxcPortfolioV1AssetBalanceQueryServiceAssetBalanceGet({ accountId, assetId });
  const showPending = Boolean(isPending || error);

  if (showPending) {
    return <Skeleton charCount={8} />;
  }

  const defaultValue = `Asset ${getShortenedId(assetId)}`;

  if (!data?.assetBalance?.data?.asset?.data?.type) {
    return defaultValue;
  }

  if ('equity' in data.assetBalance.data.asset.data.type || 'moneyMarket' in data.assetBalance.data.asset.data.type) {
    return (
      getAssetName(data.assetBalance.data.asset.data.type) ||
      getAssetSymbol(data.assetBalance.data.asset.data.type) ||
      getAssetCusip(data.assetBalance.data.asset.data.type) ||
      `Asset ${getShortenedId(assetId)}`
    );
  }

  // See CashBalance
  if ('cash' in data.assetBalance.data.asset.data.type) {
    return undefined;
  }

  exhaustiveCheck(data.assetBalance.data.asset.data.type);

  return defaultValue;
}

export const Route = createFileRoute('/_app/_clearing-core/registration/account/$accountId/_account/_detail/asset/_asset/$assetId/_asset-balance')({
  loader: ({ params }) =>
    ({
      breadcrumb: buildBreadcrumb({
        id: `asset-balance-${params.accountId}-${params.assetId}`,
        prefix: <AssetLogoBreadcrumb assetId={params.assetId} />,
        label: <AssetBalanceBreadcrumb accountId={params.accountId} assetId={params.assetId} />,
        to: getPortfolioAssetBalanceDetailPath(params.accountId, params.assetId),
        suffix: <CopyButton content={params.assetId} label="Copy Asset ID" tooltip={{ copy: params.assetId, copied: 'Copied Asset ID' }} />,
      }),
    }) satisfies AggregateBreadcrumb,
  head: () => getPageHead('Account Asset'),
  component: AssetBalanceLayout,
});

const errorMessageGetter = buildDefaultErrorMessageGetter('Failed to load asset balance', 'view this asset balance');

function AssetBalanceLayout() {
  const { accountId, assetId } = Route.useParams();
  const request = { accountId, assetId } as const;
  const { data, error, isPending } = useIxcPortfolioV1AssetBalanceQueryServiceAssetBalanceGet(request);
  useErrorHandler(error, errorMessageGetter);
  const showPending = Boolean(isPending || error);

  return (
    <PageLayout gap={7}>
      <PageLayout.Section noBorder>
        <RegistrationAccountPageHeader
          accountId={accountId}
          actions={<PortfolioAssetBalanceEntityMenu accountId={accountId} assetId={assetId} />}
          pills={<PortfolioAssetBalanceStatusPill isPending={showPending} size="large" status={data?.assetBalance?.status} />}
        />
      </PageLayout.Section>

      <PageLayout.Section noBorder>
        <AssetBalanceBalanceBar balance={data?.assetBalance?.data} />
      </PageLayout.Section>

      <Outlet />
    </PageLayout>
  );
}
